import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Bars } from "react-loader-spinner";
import axiosInstance from "../../Shared/axiosInstance.js"; // استيراد axiosInstance المركزي

export default function PurchaseSupplierSummary() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [purchases, setPurchases] = useState([]);
  const [supplierName, setSupplierName] = useState("");
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchPurchases = async (page = 1, size = 10) => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(
        `/Purchase/by-supplier/${id}?pageNumber=${page}&pageSize=${size}`
      );

      setPurchases(data.data || []);
      setTotalCount(data.totalCount || 0);
      setTotalPages(data.totalPages || 0);
      setCurrentPage(data.currentPage || 1);
      
      if (data.data && data.data.length > 0) {
        setSupplierName(data.data[0].supplierName);
      }
    } catch (err) {
      console.error("Error fetching supplier purchases:", err);
      Swal.fire("خطأ", "فشل في تحميل مشتريات المورد", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchases(1, pageSize);
  }, [id]);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    fetchPurchases(newPage, pageSize);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    fetchPurchases(1, newSize);
  };

  if (loading && purchases.length === 0) {
    return (
      <div className="vh-100 d-flex align-items-center justify-content-center flex-column">
        <Bars height="80" width="80" color="#2563eb" ariaLabel="loading" />
        <p className="mt-3" style={{ color: "#64748b", fontSize: "18px" }}>
          جارٍ تحميل المشتريات...
        </p>
      </div>
    );
  }

  return (
    <div className="container py-5" style={{ maxWidth: "1400px" }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 mt-5 pt-5">
        <div>
          <h2 style={{ color: "#2563eb", fontWeight: "700", fontSize: "28px", marginBottom: "4px" }}>
            مشتريات المورد {supplierName && `- ${supplierName}`}
          </h2>
          <small style={{ color: "#64748b", fontSize: "14px" }}>
            عرض {purchases.length} من أصل {totalCount} مشتريات
          </small>
        </div>
        <button
          onClick={() => navigate("/suppliers")}
          style={{
            background: "#fff",
            color: "#475569",
            padding: "10px 20px",
            borderRadius: "6px",
            border: "1px solid #bfdbfe",
            fontWeight: "600",
            fontSize: "15px",
            cursor: "pointer",
            transition: "all 0.3s ease",
          }}
          onMouseOver={(e) => e.target.style.background = "#f8fafc"}
          onMouseOut={(e) => e.target.style.background = "#fff"}
        >
          <i className="bi bi-arrow-left me-1"></i> العودة إلى الموردين
        </button>
      </div>

      {/* Info Bar */}
      {purchases.length > 0 && (
        <div className="d-flex justify-content-between align-items-center mb-3">
          <span style={{ color: "#64748b", fontSize: "14px" }}>
            عرض {((currentPage - 1) * pageSize) + 1} إلى{" "}
            {Math.min(currentPage * pageSize, totalCount)} من أصل {totalCount} مشتريات
          </span>
          <select
            value={pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            disabled={loading}
            style={{
              padding: "8px 12px",
              border: "1px solid #bfdbfe",
              borderRadius: "6px",
              fontSize: "14px",
              color: "#475569",
              fontWeight: "600",
              outline: "none",
              cursor: loading ? "not-allowed" : "pointer",
              background: "#fff",
              opacity: loading ? 0.6 : 1,
            }}
          >
            <option value="5">5 لكل صفحة</option>
            <option value="10">10 لكل صفحة</option>
            <option value="20">20 لكل صفحة</option>
            <option value="50">50 لكل صفحة</option>
          </select>
        </div>
      )}

      {/* Purchases Table */}
      {purchases.length > 0 ? (
        <>
          <div className="table-responsive">
            <table
              className="table mb-0"
              style={{
                border: "1px solid #dbeafe",
                borderRadius: "8px",
                overflow: "hidden",
                boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)",
                textAlign: "center",
                opacity: loading ? 0.6 : 1,
                transition: "opacity 0.2s",
              }}
            >
              <thead style={{ background: "#dbeafe" }}>
                <tr>
                  <th style={thStyle}>#</th>
                  <th style={thStyle}>المورد</th>
                  <th style={thStyle}>التاريخ</th>
                  <th style={thStyle}>المبلغ الإجمالي</th>
                  <th style={thStyle}>المدفوع</th>
                  <th style={thStyle}>المتبقي</th>
                  <th style={thStyle}>الحالة</th>
                  <th style={thStyle}>الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((p, index) => (
                  <tr key={p.id} style={{ background: index % 2 === 0 ? "#f8fafc" : "#ffffff" }}>
                    <td style={tdStyle}>{((currentPage - 1) * pageSize) + index + 1}</td>
                    <td style={{ ...tdStyle, fontWeight: 700, color: "#2563eb" }}>
                      {p.supplierName}
                    </td>
                    <td style={tdStyle}>{new Date(p.purchaseDate).toLocaleDateString()}</td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          background: "#bbf7d0",
                          color: "#14532d",
                          padding: "6px 12px",
                          borderRadius: "4px",
                          fontSize: "14px",
                          fontWeight: "700",
                        }}
                      >
                        {p.totalAmount.toFixed(2)} ج.م
                      </span>
                    </td>
                    <td style={{ ...tdStyle, color: "#2563eb", fontWeight: 700 }}>
                      {p.paidAmount.toFixed(2)} ج.م
                    </td>
                    <td style={{ ...tdStyle, color: "#dc2626", fontWeight: 700 }}>
                      {p.remainingAmount.toFixed(2)} ج.م
                    </td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          background:
                            p.paymentStatus === "Paid"
                              ? "#bbf7d0"
                              : p.paymentStatus === "PartiallyPaid"
                              ? "#fef3c7"
                              : "#fecaca",
                          color:
                            p.paymentStatus === "Paid"
                              ? "#14532d"
                              : p.paymentStatus === "PartiallyPaid"
                              ? "#92400e"
                              : "#991b1b",
                          padding: "4px 10px",
                          borderRadius: "4px",
                          fontSize: "17px",
                          fontWeight: "600",
                        }}
                      >
                        {p.paymentStatus === "PartiallyPaid" ? "مدفوع جزئيًا" : p.paymentStatus === "Paid" ? "مدفوع" : "غير مدفوع"}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <div className="d-flex gap-2 justify-content-center flex-wrap">
                        <button
                          style={actionBtnStyle("#2563eb")}
                          onClick={() => navigate(`/purchases/${p.id}`)}
                        >
                          <i className="bi bi-eye me-1"></i> التفاصيل
                        </button>
                        <button
                          style={actionBtnStyle("#22c55e")}
                          onClick={() => navigate(`/purchases/${p.id}/add-payment`)}
                        >
                          <i className="bi bi-plus-circle me-1"></i> إضافة دفعة
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-4">
              <div style={{ color: "#64748b", fontSize: "14px" }}>
                الصفحة {currentPage} من {totalPages}
              </div>

              <nav>
                <ul className="pagination mb-0">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      style={{
                        ...paginationBtnStyle,
                        cursor: currentPage === 1 ? "not-allowed" : "pointer",
                        opacity: currentPage === 1 ? 0.5 : 1,
                      }}
                    >
                      السابق
                    </button>
                  </li>

                  {[...Array(totalPages)].map((_, i) => {
                    const pageNum = i + 1;
                    if (
                      pageNum === 1 ||
                      pageNum === totalPages ||
                      (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                    ) {
                      return (
                        <li key={pageNum}>
                          <button
                            onClick={() => handlePageChange(pageNum)}
                            style={{
                              ...paginationBtnStyle,
                              background: currentPage === pageNum ? "#2563eb" : "#fff",
                              color: currentPage === pageNum ? "#fff" : "#475569",
                              fontWeight: currentPage === pageNum ? "600" : "500",
                            }}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    } else if (pageNum === currentPage - 3 || pageNum === currentPage + 3) {
                      return (
                        <li key={pageNum}>
                          <span style={{ ...paginationBtnStyle, cursor: "default", border: "none" }}>
                            ...
                          </span>
                        </li>
                      );
                    }
                    return null;
                  })}

                  <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      style={{
                        ...paginationBtnStyle,
                        cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                        opacity: currentPage === totalPages ? 0.5 : 1,
                      }}
                    >
                      التالي
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </>
      ) : (
        <div
          style={{
            background: "#fff",
            border: "1px solid #dbeafe",
            borderRadius: "8px",
            padding: "60px 20px",
            textAlign: "center",
            boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)",
          }}
        >
          <i
            className="bi bi-cart-check"
            style={{ fontSize: "64px", color: "#94a3b8", marginBottom: "20px" }}
          ></i>
          <h4 style={{ color: "#475569", fontWeight: "600", marginBottom: "8px" }}>
            لا توجد مشتريات
          </h4>
          <p style={{ color: "#64748b", fontSize: "15px", marginBottom: 0 }}>
            هذا المورد لا يحتوي على أي سجلات مشتريات بعد
          </p>
        </div>
      )}
    </div>
  );
}

// Styles
const thStyle = {
  color: "#1e40af",
  fontSize: "14px",
  padding: "12px 8px",
  fontWeight: "600",
  border: "1px solid #bfdbfe",
};

const tdStyle = {
  padding: "12px 8px",
  border: "1px solid #e0e7ff",
  fontSize: "17px",
  fontWeight: "600",
  color: "#475569",
};

const actionBtnStyle = (color) => ({
  background: color,
  color: "#fff",
  padding: "6px 12px",
  borderRadius: "6px",
  border: "none",
  fontWeight: "600",
  fontSize: "17px",
  cursor: "pointer",
  transition: "all 0.3s ease",
});

const paginationBtnStyle = {
  padding: "8px 14px",
  margin: "0 3px",
  border: "1px solid #bfdbfe",
  borderRadius: "6px",
  fontSize: "14px",
  cursor: "pointer",
  transition: "all 0.2s ease",
};
